import React, { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import {
  VideoCamera, Sparkle, ArrowsClockwise, Play, Upload,
  DownloadSimple, CheckCircle, CaretDown, FloppyDisk,
  Clock, Trash, Image, X, WarningCircle, ShareFat
} from '@phosphor-icons/react';
import DashboardLayout from '../components/DashboardLayout';
import BrandSettingsSummary from '../components/BrandSettingsSummary';
import ReferenceMediaUpload from '../components/ReferenceMediaUpload';
import { EarlyAccessBanner } from '../components/EarlyAccessBanner';
import { shareOrDownload, downloadFile } from '../utils/share';
import { useUpgrade } from '../components/UpgradeSystem';
import { pixelVideoGenerated } from '../utils/pixel';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

/* ═══ Voice Preview Button ═══ */
const VoicePreviewBtn = ({ voiceId }) => {
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);
  const handlePlay = async (e) => {
    e.stopPropagation();
    if (playing && audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setPlaying(false);
      return;
    }
    try {
      setPlaying(true);
      const token = localStorage.getItem('bf_access_token') || localStorage.getItem('token');
      const url = `${API}/api/voice-preview/${voiceId}`;
      const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) throw new Error('Preview failed');
      const blob = await resp.blob();
      const audioUrl = URL.createObjectURL(blob);
      if (audioRef.current) { audioRef.current.pause(); }
      const audio = new Audio(audioUrl);
      audioRef.current = audio;
      audio.onended = () => setPlaying(false);
      audio.play();
    } catch {
      setPlaying(false);
    }
  };
  return (
    <span onClick={handlePlay} data-testid={`voice-preview-${voiceId}`}
      className="cursor-pointer inline-flex items-center justify-center w-5 h-5 rounded-full transition-all hover:scale-110"
      style={{ background: playing ? 'rgba(168,139,250,0.2)' : 'rgba(255,255,255,0.06)' }}
      title={`Preview ${voiceId}`}>
      <Play size={8} weight="fill" style={{ color: playing ? '#a78bfa' : 'rgba(245,240,232,0.4)' }} />
    </span>
  );
};

const PLATFORMS = [
  { id: 'instagram', label: 'Reels', icon: '📱', aspect: '9:16' },
  { id: 'tiktok', label: 'TikTok', icon: '🎵', aspect: '9:16' },
  { id: 'youtube', label: 'Shorts', icon: '▶', aspect: '9:16' },
  { id: 'facebook', label: 'Facebook', icon: '📘', aspect: '16:9' },
];

const DURATIONS = [
  { sec: 8, credits: 8, label: '8s', clips: 1 },
  { sec: 12, credits: 12, label: '12s', clips: 1 },
];

const STYLES = [
  { id: 'product_showcase', name: 'Product Showcase', desc: 'Clean studio aesthetic', color: '#c8f060' },
  { id: 'ugc', name: 'UGC Style', desc: 'Casual, authentic feel', color: '#f59e0b' },
  { id: 'cinematic', name: 'Cinematic', desc: 'Dramatic & bold', color: '#a78bfa' },
  { id: 'talking_head', name: 'Talking Head', desc: 'Person speaking to camera', color: '#22d3ee' },
];

/* ═══ Card Wrapper ═══ */
const Card = ({ title, children, className = '' }) => (
  <div className={`rounded-xl p-5 ${className}`} style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.06)' }}>
    {title && <h3 className="text-sm font-semibold mb-4" style={{ color: '#f5f0e8' }}>{title}</h3>}
    {children}
  </div>
);

/* ═══ Input Component ═══ */
const FieldInput = ({ label, helper, value, onChange, placeholder, maxLen, multiline, testId }) => (
  <div className="mb-4">
    <label className="text-[11px] font-medium uppercase tracking-wider mb-1.5 block" style={{ color: 'rgba(245,240,232,0.3)' }}>{label}</label>
    {multiline ? (
      <textarea
        data-testid={testId}
        rows={3}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        maxLength={maxLen}
        className="w-full px-3 py-2.5 rounded-lg text-sm resize-none"
        style={{ background: '#0a0a0a', border: '1px solid rgba(255,255,255,0.08)', color: '#f5f0e8' }}
      />
    ) : (
      <input
        data-testid={testId}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        maxLength={maxLen}
        className="w-full px-3 py-2.5 rounded-lg text-sm"
        style={{ background: '#0a0a0a', border: '1px solid rgba(255,255,255,0.08)', color: '#f5f0e8' }}
      />
    )}
    {helper && <p className="text-[10px] mt-1" style={{ color: 'rgba(245,240,232,0.2)' }}>{helper}</p>}
    {maxLen && <p className="text-[10px] mt-1 text-right" style={{ color: 'rgba(245,240,232,0.15)' }}>{(value || '').length}/{maxLen}</p>}
  </div>
);

export default function AdCreatorPage() {
  const { handleApiError, checkCreditsBeforeAction } = useUpgrade();
  const [tab, setTab] = useState('create');

  // Form state
  const [productName, setProductName] = useState('');
  const [productDesc, setProductDesc] = useState('');
  const [hookLine, setHookLine] = useState('');
  const [mainMsg, setMainMsg] = useState('');
  const [ctaText, setCtaText] = useState('');
  const [platform, setPlatform] = useState('instagram');
  const [duration, setDuration] = useState(8);
  // Only 8s is currently exposed in the UI; coerce any persisted value to 8.
  useEffect(() => {
    if (duration !== 8) setDuration(8);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [style, setStyle] = useState('product_showcase');
  const [refImages, setRefImages] = useState([]);
  const [refFileIds, setRefFileIds] = useState([]);
  const [ideaLoading, setIdeaLoading] = useState(false);
  const [selectedVoice, setSelectedVoice] = useState('alloy');

  // Generation state
  const [generating, setGenerating] = useState(false);
  // Zero Friction: hide Format & Style (platform/duration/voice/style) behind Customise
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [progressStatus, setProgressStatus] = useState('');
  const [progressPct, setProgressPct] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const [jobId, setJobId] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [history, setHistory] = useState([]);

  const pollRef = useRef(null);
  const timerRef = useRef(null);
  const fileInputRef = useRef(null);

  // Load history + check for in-progress jobs
  useEffect(() => {
    loadHistory();
    checkInProgressJobs();
    // Read batch prefill from Marketing Coach
    try {
      const raw = sessionStorage.getItem('batch_prefill');
      if (raw) {
        const { type, data } = JSON.parse(raw);
        if (type === 'video_ad' && data) {
          sessionStorage.removeItem('batch_prefill');
          if (data.product) setProductName(data.product);
          if (data.headline) setHookLine(data.headline);
          if (data.ad_copy) setMainMsg(data.ad_copy);
          if (data.style_notes) {
            const matchedStyle = STYLES.find(s => s.id === data.style_notes || s.name.toLowerCase().includes(data.style_notes.toLowerCase()));
            if (matchedStyle) setStyle(matchedStyle.id);
          }
          toast.success('Video Ad pre-filled from Marketing Coach!');
        }
      }
    } catch {}
    return () => { clearInterval(pollRef.current); clearInterval(timerRef.current); };
  }, []);

  // Auto-resume polling for in-progress video jobs
  const checkInProgressJobs = async () => {
    try {
      const token = localStorage.getItem('bf_access_token') || localStorage.getItem('token');
      const { data } = await axios.get(`${API}/api/video-ads/in-progress`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (data.in_progress?.length > 0) {
        const activeJob = data.in_progress[0];
        setJobId(activeJob.job_id);
        setGenerating(true);
        setProgressStatus(activeJob.status || 'generating');
        setProgressPct(activeJob.progress || 0);
        toast.info('Resuming video generation in progress...');
        resumePolling(activeJob.job_id);
      }
    } catch {}
  };

  const resumePolling = (resumeJobId) => {
    const token = localStorage.getItem('bf_access_token') || localStorage.getItem('token');
    const startTime = Date.now();
    timerRef.current = setInterval(() => setElapsed(e => e + 1), 1000);
    pollRef.current = setInterval(async () => {
      try {
        const elapsedMs = Date.now() - startTime;
        if (elapsedMs > 600000) {
          clearInterval(pollRef.current);
          clearInterval(timerRef.current);
          setError('Video generation timed out. Your credits have been refunded. Try a shorter, simpler prompt.');
          setGenerating(false);
          return;
        }
        const { data: status } = await axios.get(`${API}/api/video-ads/status/${resumeJobId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const s = typeof status.status === 'string' ? status.status : 'generating';
        setProgressStatus(s);
        setProgressPct(status.progress || 0);
        if (s === 'completed') {
          clearInterval(pollRef.current);
          clearInterval(timerRef.current);
          setResult(status);
          setGenerating(false);
          loadHistory();
        } else if (s === 'failed') {
          clearInterval(pollRef.current);
          clearInterval(timerRef.current);
          setError(status.error || 'Generation failed.');
          setGenerating(false);
        }
      } catch {}
    }, 4000);
  };

  const loadHistory = async () => {
    try {
      const { data } = await axios.get(`${API}/api/video-ads/history`);
      setHistory(data.videos || data.ads || []);
    } catch {}
  };

  // Upload reference image
  const uploadRef = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    const preview = URL.createObjectURL(file);
    setRefImages(prev => [...prev, preview]);

    const form = new FormData();
    form.append('file', file);
    try {
      const token = localStorage.getItem('bf_access_token') || localStorage.getItem('token');
      const { data } = await axios.post(`${API}/api/uploads/upload`, form, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      setRefFileIds(prev => [...prev, data.file_id]);
    } catch {
      setError('Failed to upload image.');
    }
  };

  const removeRef = (idx) => {
    setRefImages(prev => prev.filter((_, i) => i !== idx));
    setRefFileIds(prev => prev.filter((_, i) => i !== idx));
  };

  // Write this for me
  const generateIdea = async () => {
    setIdeaLoading(true);
    try {
      const { data } = await axios.post(`${API}/api/generate-idea`, {
        type: 'video_ad',
        product: productName,
        description: productDesc,
      });
      const idea = data.idea;
      if (idea.product_name) setProductName(idea.product_name);
      if (idea.product_desc) setProductDesc(idea.product_desc);
      if (idea.hook_line) setHookLine(idea.hook_line);
      if (idea.main_message) setMainMsg(idea.main_message);
      if (idea.cta_text) setCtaText(idea.cta_text);
      if (idea.style) {
        const match = STYLES.find(s => s.id === idea.style);
        if (match) setStyle(match.id);
      }
      if (idea.platform) {
        const match = PLATFORMS.find(p => p.id === idea.platform);
        if (match) setPlatform(match.id);
      }
      toast.success('AI idea generated! Review and tweak before generating.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate idea');
    }
    setIdeaLoading(false);
  };

  // Generate
  const generate = async () => {
    if (!productName.trim() && !hookLine.trim()) {
      setError('Add a product name or hook line to generate.');
      return;
    }
    const hasCredits = await checkCreditsBeforeAction('video_ad');
    if (!hasCredits) return;

    setGenerating(true);
    setError('');
    setResult(null);
    setProgressStatus('Starting generation...');
    setProgressPct(0);
    setElapsed(0);

    // Timer for elapsed seconds
    timerRef.current = setInterval(() => setElapsed(e => e + 1), 1000);

    try {
      const token = localStorage.getItem('bf_access_token') || localStorage.getItem('token');
      const aspectMap = { 'instagram': 'portrait', 'tiktok': 'portrait', 'youtube': 'portrait', 'facebook': 'landscape' };
      const { data } = await axios.post(`${API}/api/video-ads/create`, {
        product: productName || 'Product',
        headline: hookLine || productName,
        ad_copy: mainMsg || productDesc,
        platform,
        style_notes: style,
        duration,
        aspect: aspectMap[platform] || 'portrait',
        reference_file_ids: refFileIds,
        fidelity_mode: refFileIds.length > 0 ? 'high_similarity' : 'creative',
        voice: selectedVoice,
      }, { headers: { Authorization: `Bearer ${token}` } });

      setJobId(data.job_id);

      // Poll for real status from backend
      const startTime = Date.now();
      pollRef.current = setInterval(async () => {
        try {
          // Client-side timeout: give up after 10 minutes (Sora 2 can take 3-8 min)
          const elapsedMs = Date.now() - startTime;
          if (elapsedMs > 600000) {
            clearInterval(pollRef.current);
            clearInterval(timerRef.current);
            setError('Video generation timed out. Your credits have been refunded. Try a shorter, simpler prompt.');
            setGenerating(false);
            return;
          }

          const { data: status } = await axios.get(`${API}/api/video-ads/status/${data.job_id}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          // Update progress from backend
          const s = typeof status.status === 'string' ? status.status : 'generating';
          setProgressStatus(s);
          setProgressPct(status.progress || 0);

          if (s === 'completed') {
            clearInterval(pollRef.current);
            clearInterval(timerRef.current);
            setResult(status);
            setGenerating(false);
            loadHistory();
            pixelVideoGenerated();
          } else if (s === 'failed') {
            clearInterval(pollRef.current);
            clearInterval(timerRef.current);
            setError(status.error || 'Generation failed.');
            setGenerating(false);
          }
        } catch {}
      }, 4000);

    } catch (err) {
      clearInterval(timerRef.current);
      if (!handleApiError(err)) {
        const d = err.response?.data?.detail;
        setError(typeof d === 'string' ? d : (d?.message || 'Failed to start generation.'));
      }
      setGenerating(false);
    }
  };

  const currentStyle = STYLES.find(s => s.id === style) || STYLES[0];

  return (
    <DashboardLayout>
      <div className="space-y-5" data-testid="video-ad-creator-page">
      <BrandSettingsSummary fields={['brand','industry','tone']} />
        <EarlyAccessBanner />

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl lg:text-3xl font-semibold" style={{ color: '#f5f0e8', fontFamily: "'Instrument Serif', serif" }}>
              Video Ad Creator
            </h1>
            <p className="text-sm mt-1" style={{ color: 'rgba(245,240,232,0.35)' }}>
              Create high-converting short-form video ads (8s or 12s) optimized for social platforms.
            </p>
          </div>
          <div className="flex gap-2">
            {['create', 'history'].map(t => (
              <button key={t} onClick={() => setTab(t)} data-testid={`tab-${t}`}
                className="px-4 py-2 rounded-lg text-xs font-medium transition-all"
                style={{
                  background: tab === t ? 'rgba(200,240,96,0.08)' : 'transparent',
                  border: `1px solid ${tab === t ? 'rgba(200,240,96,0.2)' : 'rgba(255,255,255,0.06)'}`,
                  color: tab === t ? '#c8f060' : 'rgba(245,240,232,0.4)',
                }}>
                {t === 'create' ? 'Create' : 'My Videos'}
              </button>
            ))}
          </div>
        </div>

        {tab === 'create' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {/* LEFT: Creative Brief */}
            <div className="space-y-4">
              <Card title="Your Product or Service">
                <FieldInput label="Product Name" value={productName} onChange={setProductName}
                  placeholder="ScalitOS — Your Marketing Team" testId="product-name-input" />
                <FieldInput label="What makes it special" value={productDesc} onChange={setProductDesc}
                  placeholder="We give founders a daily marketing plan and generate content in their brand voice"
                  multiline testId="product-desc-input" />

                {/* Write this for me */}
                <button onClick={generateIdea} disabled={ideaLoading} data-testid="ai-generate-idea-btn"
                  className="w-full py-2.5 rounded-lg text-xs font-medium flex items-center justify-center gap-2 mb-4 transition-all"
                  style={{
                    background: 'rgba(168,139,250,0.08)',
                    border: '1px solid rgba(168,139,250,0.2)',
                    color: '#a78bfa',
                    opacity: ideaLoading ? 0.6 : 1,
                  }}>
                  {ideaLoading ? <ArrowsClockwise size={14} className="animate-spin" /> : <Sparkle size={14} weight="fill" />}
                  {ideaLoading ? 'Generating idea...' : 'Write this for me'}
                </button>

                {/* Reference Images */}
                <div>
                  <label className="text-[11px] font-medium uppercase tracking-wider mb-2 block" style={{ color: 'rgba(245,240,232,0.3)' }}>Reference Images</label>
                  <div className="flex gap-2 flex-wrap">
                    {refImages.map((img, i) => (
                      <div key={i} className="relative w-20 h-20 rounded-lg overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.08)' }}>
                        <img src={img} alt="" className="w-full h-full object-cover" />
                        <button onClick={() => removeRef(i)} className="absolute top-1 right-1 w-5 h-5 rounded-full flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.7)' }}>
                          <X size={10} style={{ color: '#f5f0e8' }} />
                        </button>
                      </div>
                    ))}
                    {refImages.length < 3 && (
                      <button onClick={() => fileInputRef.current?.click()} data-testid="upload-ref-btn"
                        className="w-20 h-20 rounded-lg flex flex-col items-center justify-center gap-1"
                        style={{ border: '1px dashed rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.02)' }}>
                        <Upload size={16} style={{ color: 'rgba(245,240,232,0.2)' }} />
                        <span className="text-[9px]" style={{ color: 'rgba(245,240,232,0.15)' }}>Add</span>
                      </button>
                    )}
                  </div>
                  <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={uploadRef} />
                  {refImages.length > 0 && (
                    <p className="text-[10px] mt-2 px-2 py-1 rounded" style={{ background: 'rgba(200,240,96,0.06)', color: '#c8f060' }}>
                      Reference images improve product accuracy
                    </p>
                  )}
                </div>
              </Card>

              <Card title="Your Ad Message">
                <FieldInput label="Hook / Headline" value={hookLine} onChange={setHookLine}
                  placeholder="Stop guessing what to post" maxLen={60} testId="hook-input" />
                <FieldInput label="Main Message" value={mainMsg} onChange={setMainMsg}
                  placeholder="ScalitOS gives you a daily action plan, generates content in your voice, and helps you outgrow competitors"
                  multiline testId="main-msg-input" />
                <FieldInput label="Call to Action" value={ctaText} onChange={setCtaText}
                  placeholder="Start free today" testId="cta-input" />
              </Card>

              <button onClick={() => setShowAdvanced(v => !v)} data-testid="ad-customise-toggle"
                className="w-full text-left px-4 py-2.5 rounded-xl text-[11px] font-semibold flex items-center justify-between mb-2"
                style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', color: 'rgba(245,240,232,0.55)' }}>
                <span style={{ fontFamily: 'ui-monospace, monospace', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                  {showAdvanced ? 'Hide format & style' : 'Customise format & style →'}
                </span>
                <span style={{ color: '#c8f060' }}>{showAdvanced ? '−' : '+'}</span>
              </button>

              {showAdvanced && (
              <Card title="Format & Style">
                {/* Platform */}
                <label className="text-[11px] font-medium uppercase tracking-wider mb-2 block" style={{ color: 'rgba(245,240,232,0.3)' }}>Platform</label>
                <div className="flex gap-2 mb-4">
                  {PLATFORMS.map(p => (
                    <button key={p.id} onClick={() => setPlatform(p.id)} data-testid={`platform-${p.id}`}
                      className="flex-1 py-2.5 rounded-lg text-xs font-medium text-center transition-all"
                      style={{
                        background: platform === p.id ? 'rgba(200,240,96,0.08)' : 'rgba(255,255,255,0.02)',
                        border: `1px solid ${platform === p.id ? 'rgba(200,240,96,0.25)' : 'rgba(255,255,255,0.06)'}`,
                        color: platform === p.id ? '#c8f060' : 'rgba(245,240,232,0.35)',
                      }}>
                      <span className="text-base">{p.icon}</span><br />{p.label}
                    </button>
                  ))}
                </div>

                {/* Duration */}
                <label className="text-[11px] font-medium uppercase tracking-wider mb-2 block" style={{ color: 'rgba(245,240,232,0.3)' }}>Duration</label>
                <div className="flex gap-2 mb-4">
                  {DURATIONS.map(d => (
                    <button key={d.sec} onClick={() => setDuration(d.sec)} data-testid={`duration-${d.sec}`}
                      className="flex-1 py-3 rounded-lg text-center transition-all"
                      style={{
                        background: duration === d.sec ? 'rgba(200,240,96,0.08)' : 'rgba(255,255,255,0.02)',
                        border: `1px solid ${duration === d.sec ? 'rgba(200,240,96,0.25)' : 'rgba(255,255,255,0.06)'}`,
                      }}>
                      <span className="text-sm font-semibold block" style={{ color: duration === d.sec ? '#c8f060' : '#f5f0e8' }}>{d.label}</span>
                      <span className="text-[10px] block mt-0.5" style={{ color: 'rgba(245,240,232,0.25)' }}>{d.credits} credits</span>
                    </button>
                  ))}
                </div>

                {/* Voice */}
                <label className="text-[11px] font-medium uppercase tracking-wider mb-2 block" style={{ color: 'rgba(245,240,232,0.3)' }}>Narrator Voice</label>
                <div className="grid grid-cols-3 gap-2 mb-4">
                  {[
                    { id: 'alloy', name: 'Alloy' },
                    { id: 'echo', name: 'Echo' },
                    { id: 'fable', name: 'Fable' },
                    { id: 'onyx', name: 'Onyx' },
                    { id: 'nova', name: 'Nova' },
                    { id: 'shimmer', name: 'Shimmer' },
                  ].map(v => (
                    <button key={v.id} onClick={() => setSelectedVoice(v.id)} data-testid={`voice-${v.id}`}
                      className="py-2 px-2 rounded-lg text-center text-[11px] font-medium transition-all flex items-center justify-center gap-1.5"
                      style={{
                        background: selectedVoice === v.id ? 'rgba(168,139,250,0.08)' : 'rgba(255,255,255,0.02)',
                        border: `1px solid ${selectedVoice === v.id ? 'rgba(168,139,250,0.25)' : 'rgba(255,255,255,0.06)'}`,
                        color: selectedVoice === v.id ? '#a78bfa' : 'rgba(245,240,232,0.4)',
                      }}>
                      <span>{v.name}</span>
                      <VoicePreviewBtn voiceId={v.id} />
                    </button>
                  ))}
                </div>

                {/* Style */}
                <label className="text-[11px] font-medium uppercase tracking-wider mb-2 block" style={{ color: 'rgba(245,240,232,0.3)' }}>Style</label>
                <div className="grid grid-cols-2 gap-2">
                  {STYLES.map(s => (
                    <button key={s.id} onClick={() => setStyle(s.id)} data-testid={`style-${s.id}`}
                      className="p-3 rounded-lg text-left transition-all"
                      style={{
                        background: style === s.id ? `${s.color}08` : 'rgba(255,255,255,0.02)',
                        border: `1px solid ${style === s.id ? `${s.color}30` : 'rgba(255,255,255,0.06)'}`,
                      }}>
                      <p className="text-xs font-semibold" style={{ color: style === s.id ? s.color : '#f5f0e8' }}>{s.name}</p>
                      <p className="text-[10px] mt-0.5" style={{ color: 'rgba(245,240,232,0.25)' }}>{s.desc}</p>
                    </button>
                  ))}
                </div>
              </Card>
              )}

              {/* Error */}
              {error && (
                <div className="flex items-center gap-2 p-3 rounded-lg" style={{ background: 'rgba(255,68,68,0.06)', border: '1px solid rgba(255,68,68,0.12)' }}>
                  <WarningCircle size={14} style={{ color: '#ff6b6b' }} />
                  <p className="text-xs" style={{ color: '#ff6b6b' }}>{typeof error === 'string' ? error : 'An error occurred'}</p>
                </div>
              )}

              {/* Reference media (optional) */}
              <ReferenceMediaUpload
                label="Add reference media (optional)"
                note="Upload product footage, photos, or brand assets to include in your video."
                acceptedTypes={['image', 'video']}
                maxFiles={3}
                maxSizeMB={25}
              />

              {/* Generate Button */}
              <button onClick={generate} disabled={generating} data-testid="generate-video-btn"
                className="w-full py-4 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 transition-all"
                style={{
                  background: generating ? 'rgba(200,240,96,0.06)' : '#c8f060',
                  color: generating ? '#c8f060' : '#080808',
                  opacity: generating ? 0.7 : 1,
                }}>
                {generating ? (
                  <><ArrowsClockwise size={16} className="animate-spin" /> Creating your video…</>
                ) : (
                  <><Sparkle size={16} weight="fill" /> Make Video Ad &middot; 8 credits</>
                )}
              </button>
            </div>

            {/* RIGHT: Preview Panel */}
            <div className="space-y-4">
              <Card>
                <h3 className="text-sm font-semibold mb-4" style={{ color: '#f5f0e8' }}>Preview</h3>

                {/* Phone Frame */}
                <div className="mx-auto" style={{ maxWidth: platform === 'facebook' ? '100%' : '280px' }}>
                  <div className="rounded-2xl overflow-hidden" style={{
                    aspectRatio: platform === 'facebook' ? '16/9' : '9/16',
                    background: '#0a0a0a',
                    border: '2px solid rgba(255,255,255,0.06)',
                    maxHeight: '480px',
                  }}>
                    {/* Generating state */}
                    {generating && (
                      <div className="flex flex-col items-center justify-center h-full p-6 text-center">
                        <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ background: `${currentStyle.color}10` }}>
                          <ArrowsClockwise size={20} className="animate-spin" style={{ color: currentStyle.color }} />
                        </div>

                        {/* Progress bar */}
                        <div className="w-full max-w-[200px] h-1.5 rounded-full mb-3" style={{ background: 'rgba(255,255,255,0.06)' }}>
                          <div className="h-full rounded-full transition-all duration-700" style={{ width: `${progressPct}%`, background: '#c8f060' }} />
                        </div>

                        {/* Staged status messages */}
                        <p className="text-xs font-medium mb-1" style={{ color: '#f5f0e8' }}>
                          {elapsed < 5 ? 'Analyzing creative brief...' :
                           elapsed < 15 ? 'Building scene composition...' :
                           elapsed < 45 ? 'Generating video with Sora 2...' :
                           elapsed < 120 ? 'Rendering frames (this typically takes 2-5 min)...' :
                           elapsed < 300 ? 'Still rendering — Sora 2 is processing your video...' :
                           elapsed < 480 ? 'Almost done — finalizing your video...' :
                           'Wrapping up — video should be ready any moment...'}
                        </p>
                        <p className="text-[10px] mt-1" style={{ color: 'rgba(245,240,232,0.25)' }}>
                          {Math.floor(elapsed / 60)}:{(elapsed % 60).toString().padStart(2, '0')} elapsed
                        </p>
                        <p className="text-[10px] mt-3 px-3 py-1.5 rounded-lg" style={{ background: 'rgba(200,240,96,0.05)', color: 'rgba(200,240,96,0.6)', border: '1px solid rgba(200,240,96,0.1)' }}>
                          Safe to navigate away — we'll email you when it's ready
                        </p>
                      </div>
                    )}

                    {/* Result */}
                    {result && !generating && result.video_url && (
                      <video
                        data-testid="video-result-player"
                        src={`${API}${result.video_url}`}
                        controls
                        className="w-full h-full object-contain"
                        style={{ background: '#000' }}
                      />
                    )}

                    {/* Result mock (no video url) */}
                    {result && !generating && !result.video_url && (
                      <div className="flex flex-col items-center justify-center h-full p-6 text-center">
                        <CheckCircle size={32} weight="fill" style={{ color: '#c8f060' }} className="mb-3" />
                        <p className="text-sm font-medium mb-1" style={{ color: '#f5f0e8' }}>Generation Complete</p>
                        <p className="text-[11px]" style={{ color: 'rgba(245,240,232,0.35)' }}>
                          Runway ML API key needed for real video output. Set RUNWAY_API_KEY in environment.
                        </p>
                      </div>
                    )}

                    {/* Empty state */}
                    {!generating && !result && (
                      <div className="flex flex-col items-center justify-center h-full p-6 text-center">
                        <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-3" style={{ background: 'rgba(200,240,96,0.06)' }}>
                          <VideoCamera size={24} style={{ color: 'rgba(200,240,96,0.3)' }} />
                        </div>
                        <p className="text-xs font-medium mb-1" style={{ color: 'rgba(245,240,232,0.4)' }}>Your video will appear here</p>
                        <p className="text-[10px]" style={{ color: 'rgba(245,240,232,0.15)' }}>Fill in the brief and tap Make</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Action buttons */}
                {result && !generating && (
                  <div className="flex gap-2 mt-4">
                    {result.video_url && (
                      <>
                        <button onClick={() => window.open(`${API}${result.video_url}`, '_blank')} data-testid="download-video-btn"
                          className="flex-1 py-2.5 rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5"
                          style={{ background: '#c8f060', color: '#080808' }}>
                          <DownloadSimple size={14} /> Download
                        </button>
                        <button onClick={() => shareOrDownload(
                          result.video_url, 'video/mp4', productName || 'Video Ad'
                        )} data-testid="share-video-btn"
                          className="py-2.5 px-4 rounded-lg text-xs font-medium flex items-center justify-center gap-1.5"
                          style={{ background: 'rgba(168,139,250,0.08)', border: '1px solid rgba(168,139,250,0.2)', color: '#a78bfa' }}>
                          <ShareFat size={14} /> Share
                        </button>
                      </>
                    )}
                    <button onClick={() => { setResult(null); setJobId(null); }} data-testid="new-video-btn"
                      className="flex-1 py-2.5 rounded-lg text-xs font-medium flex items-center justify-center gap-1.5"
                      style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', color: 'rgba(245,240,232,0.5)' }}>
                      <ArrowsClockwise size={14} /> New Video
                    </button>
                  </div>
                )}
              </Card>

              {/* Quick tips */}
              <div className="p-4 rounded-xl" style={{ background: 'rgba(200,240,96,0.03)', border: '1px solid rgba(200,240,96,0.06)' }}>
                <p className="text-[11px] font-semibold mb-2" style={{ color: '#c8f060' }}>Tips for great results</p>
                <ul className="space-y-1">
                  {[
                    'Add reference images for better product accuracy',
                    'Keep hook lines under 10 words for impact',
                    '10-second videos work best for Instagram Reels',
                  ].map((tip, i) => (
                    <li key={i} className="text-[10px] flex items-start gap-1.5" style={{ color: 'rgba(245,240,232,0.3)' }}>
                      <span style={{ color: '#c8f060' }}>-</span> {tip}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* History Tab */}
        {tab === 'history' && (
          <div>
            {history.length === 0 ? (
              <div className="text-center py-20">
                <VideoCamera size={40} style={{ color: 'rgba(245,240,232,0.1)' }} className="mx-auto mb-3" />
                <p className="text-sm" style={{ color: 'rgba(245,240,232,0.3)' }}>No videos yet. Create your first video ad above.</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {history.map(ad => (
                  <div key={ad.job_id} className="rounded-xl overflow-hidden" style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.06)' }}>
                    <div className="aspect-video bg-black/50 flex items-center justify-center">
                      {ad.video_url ? (
                        <video src={`${API}${ad.video_url}`} className="w-full h-full object-cover" />
                      ) : (
                        <VideoCamera size={24} style={{ color: 'rgba(245,240,232,0.1)' }} />
                      )}
                    </div>
                    <div className="p-3">
                      <p className="text-xs font-medium truncate" style={{ color: '#f5f0e8' }}>{ad.product || 'Video Ad'}</p>
                      <p className="text-[10px] mt-1" style={{ color: 'rgba(245,240,232,0.2)' }}>
                        {ad.duration || 10}s &middot; {ad.created_at ? new Date(ad.created_at).toLocaleDateString() : ''}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
